import React, { useState, useEffect } from 'react';
import {
    Button as ChakraButton,
} from "@chakra-ui/react";
import { getUserdata } from '../Redux/Action';
import { useDispatch, useSelector } from 'react-redux';
import { useAppTheme } from '../Hooks/useAppTheme';

export const AddedAccounts = ({ refresh }) => {
    const theme = useAppTheme();
    const [activeTab, setActiveTab] = useState('crypto');
    const [currentIndex, setCurrentIndex] = useState(0);
    const dispatch = useDispatch();
    const userData = useSelector((state) => state?.Login?.userData);
    
    // Get accounts directly from Redux store
    const accounts = userData?.transactionAccount || [];
    const cryptoAccounts = accounts.filter((acc) => acc.method === 'crypto');
    const bankAccounts = accounts.filter((acc) => acc.method === 'bank');
    const visibleAccounts = activeTab === 'crypto' ? cryptoAccounts : bankAccounts;

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
    };

    const prevSlide = () => {
        setCurrentIndex((prev) => (prev === 0 ? visibleAccounts.length - 1 : prev - 1));
    };

    const nextSlide = () => {
        setCurrentIndex((prev) => (prev === visibleAccounts.length - 1 ? 0 : prev + 1));
    };

    useEffect(() => {
        setCurrentIndex(0);
    }, [activeTab]);

    const fetchUserData = () => {
        dispatch(getUserdata());
    };

    useEffect(() => {
        fetchUserData()
    }, [refresh])

    return (
        <div className="w-full flex flex-col justify-between min-h-[600px] rounded-lg p-3 sm:p-5" style={{ backgroundColor: theme.bgCard, boxShadow: theme.shadowCard }}>
            <h2 className="text-2xl font-bold mb-3" style={{ color: theme.brandPrimary }}>Saved Accounts</h2>

            {/* Tabs */}
            <div className="flex gap-4 mb-6 flex-wrap">
                <ChakraButton
                    bg={activeTab === "crypto" ? theme.brandPrimary : theme.buttonSecondaryBg}
                    border={activeTab === "crypto" ? "none" : `1px solid ${theme.buttonSecondaryBorder}`}
                    color={activeTab === "crypto" ? theme.buttonPrimaryText : theme.buttonSecondaryText}
                    _hover={{ opacity: 0.9 }}
                    padding={5}
                    onClick={() => setActiveTab("crypto")}
                    flex="1"
                    minWidth="120px"
                    whiteSpace="normal"
                    wordBreak="break-word"
                >
                    Crypto Accounts
                </ChakraButton>
                <ChakraButton
                    bg={activeTab === "bank" ? theme.brandPrimary : theme.buttonSecondaryBg}
                    border={activeTab === "bank" ? "none" : `1px solid ${theme.buttonSecondaryBorder}`}
                    color={activeTab === "bank" ? theme.buttonPrimaryText : theme.buttonSecondaryText}
                    _hover={{ opacity: 0.9 }}
                    padding={5}
                    onClick={() => setActiveTab("bank")}
                    flex="1"
                    minWidth="120px"
                    whiteSpace="normal"
                    wordBreak="break-word"
                >
                    Bank Accounts
                </ChakraButton>
            </div>

            {/* Carousel Section with Fixed Height */}
            <div className="relative w-full overflow-hidden flex-1 flex flex-col justify-start">
                <h3 className="text-lg font-semibold mb-4" style={{ color: theme.brandPrimary }}>
                    {activeTab === "crypto" ? "Crypto Wallets" : "Bank Accounts"}
                </h3>

                {visibleAccounts.length === 0 ? (
                    <div className="relative min-h-[350px] flex items-center justify-center rounded-lg border border-dashed" style={{ backgroundColor: theme.bgSecondary, borderColor: theme.borderSecondary }}>
                        <p className="text-sm" style={{ color: theme.textTertiary }}>
                            No {activeTab === "crypto" ? "crypto" : "bank"} accounts added.
                        </p>
                    </div>
                ) : (
                    <div className="relative min-h-[350px]">
                        <div
                            className="flex transition-transform duration-500 ease-in-out"
                            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                        >
                            {visibleAccounts.map((account, index) => (
                                <div key={index} className="min-w-full flex-shrink-0">
                                    <div className="p-4 sm:p-6 rounded-lg border h-full flex flex-col justify-between" style={{ backgroundColor: theme.bgCard, borderColor: theme.borderPrimary, boxShadow: theme.shadowCard }}>
                                        {activeTab === "crypto" ? (
                                            <div className="space-y-4 p-4 sm:p-10">
                                                <Field label="Wallet Address" value={account.walletAddress} onCopy={handleCopy} theme={theme} />
                                                <Field label="Chain" value={account.chain} onCopy={handleCopy} theme={theme} />
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-1 gap-4 p-4 sm:p-10">
                                                <Field label="Account Holder" value={account.name} onCopy={handleCopy} theme={theme} />
                                                <Field label="Account No" value={account.accountNo} onCopy={handleCopy} theme={theme} />
                                                <Field label="IBAN" value={account.iban} onCopy={handleCopy} theme={theme} />
                                                <Field label="IFSC" value={account.ifsc} onCopy={handleCopy} theme={theme} />
                                                <Field label="Branch Name" value={account.branchName} onCopy={handleCopy} theme={theme} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Navigation Buttons */}
                        {visibleAccounts.length > 1 && (
                            <>
                                <button
                                    onClick={prevSlide}
                                    className="absolute top-1/2 -translate-y-1/2 left-1 sm:left-4 text-white rounded-full w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center shadow transition text-sm sm:text-base z-10"
                                    style={{ backgroundColor: theme.brandPrimary }}
                                    onMouseEnter={(e) => e.target.style.opacity = '0.9'}
                                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                                >
                                    ‹
                                </button>
                                <button
                                    onClick={nextSlide}
                                    className="absolute top-1/2 -translate-y-1/2 right-1 sm:right-4 text-white rounded-full w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center shadow transition text-sm sm:text-base z-10"
                                    style={{ backgroundColor: theme.brandPrimary }}
                                    onMouseEnter={(e) => e.target.style.opacity = '0.9'}
                                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                                >
                                    ›
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>

    );
};

const Field = ({ label, value, onCopy, theme }) => {
    const [copied, setCopied] = useState(false);

    const handleClick = () => {
        onCopy(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
    };

    return (
        <div className="flex flex-col gap-1 border p-3 rounded-md hover:shadow-sm transition" style={{ borderColor: theme.borderPrimary }}>
            <div className="flex justify-between items-start">
                <p className="text-xs font-semibold" style={{ color: theme.brandPrimary }}>{label}</p>
                <button
                    onClick={handleClick}
                    className={`text-xs font-semibold ml-2 transition-colors ${copied ? 'text-green-600' : ''
                        }`}
                    style={{ color: copied ? undefined : theme.brandPrimary }}
                    onMouseEnter={(e) => { if (!copied) e.target.style.opacity = '0.8' }}
                    onMouseLeave={(e) => { if (!copied) e.target.style.opacity = '1' }}
                    title="Copy"
                >
                    {copied ? 'Copied' : 'Copy'}
                </button>
            </div>
            <p className="text-sm font-medium break-words" style={{ color: theme.textPrimary }}>{value}</p>
        </div>
    );
};